import React, { useEffect, useState } from 'react'
import './style.css'
interface CookieBotWindow extends Window {
  Cookiebot?: any
  CookieConsent?: any
}

declare const window: CookieBotWindow

const CookieBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false)
  const [showCustomiseDialog, setShowCustomiseDialog] = useState(false)
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    preferences: true,
    statistics: true,
    marketing: true
  })

  // Section visibility states
  const [visibleSections, setVisibleSections] = useState({
    necessary: false,
    preferences: false,
    statistics: false,
    marketing: false
  })

  useEffect(() => {
    // Check if Cookiebot is loaded
    const checkCookiebot = () => {
      if (window.Cookiebot) {
        // Hide the default Cookiebot dialog
        if (typeof window.Cookiebot.hide === 'function') {
          window.Cookiebot.hide()
        }

        // Alternative way to hide Cookiebot
        if (window.Cookiebot.dialog && window.Cookiebot.dialog.style) {
          window.Cookiebot.dialog.style.display = 'none'
        }

        // Also try to hide via the widget property
        if (window.Cookiebot.widget) {
          window.Cookiebot.widget.display = false
        }

        // Show our custom banner if consent hasn't been given
        if (!window.Cookiebot.hasResponse) {
          setShowBanner(true)
        }
        return true
      }
      return false
    }

    // If Cookiebot isn't loaded yet, set up a listener for when it loads
    if (!checkCookiebot()) {
      window.addEventListener('CookiebotOnLoad', () => {
        // Hide the default Cookiebot dialog
        if (window.Cookiebot) {
          // Try multiple methods to ensure Cookiebot is hidden
          if (typeof window.Cookiebot.hide === 'function') {
            window.Cookiebot.hide()
          }

          // Show our custom banner if consent hasn't been given
          if (!window.Cookiebot.hasResponse) {
            setShowBanner(true)
          }
        }
      })
    }

    // Set up event listeners for Cookiebot events
    window.addEventListener('CookiebotOnAccept', () => {
      setShowBanner(false)
    })

    // Hide Cookiebot when it tries to display
    window.addEventListener('CookiebotOnDialogDisplay', () => {
      if (window.Cookiebot) {
        // Try multiple methods to ensure Cookiebot is hidden
        if (typeof window.Cookiebot.hide === 'function') {
          window.Cookiebot.hide()
        }

        // Show our custom banner instead
        if (!window.Cookiebot.hasResponse) {
          setShowBanner(true)
        }
      }
    })

    // Add a MutationObserver to detect and hide the Cookiebot dialog if it appears in the DOM
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const node = mutation.addedNodes[i] as HTMLElement
            if (node.id === 'CookieConsentDialog' || (node.classList && node.classList.contains('CookieDeclaration'))) {
              node.style.display = 'none'
            }
          }
        }
      })
    })

    // Start observing the document body for changes
    observer.observe(document.body, { childList: true, subtree: true })

    // Clean up event listeners and observer on component unmount
    return () => {
      window.removeEventListener('CookiebotOnLoad', () => {})
      window.removeEventListener('CookiebotOnAccept', () => {})
      window.removeEventListener('CookiebotOnDialogDisplay', () => {})
      observer.disconnect()
    }
  }, [])

  const handleAcceptAll = () => {
    if (window.Cookiebot) {
      // window.Cookiebot.submitConsent()
      window.Cookiebot.submitCustomConsent(true, true, true)
      setShowBanner(false)
    }
  }

  const handleCustomise = () => {
    setShowCustomiseDialog(true)
  }

  const handleAcceptSelected = () => {
    if (window.Cookiebot) {
      window.Cookiebot.submitCustomConsent(
        cookiePreferences.preferences,
        cookiePreferences.statistics,
        cookiePreferences.marketing
      )
      setShowBanner(false)
    } else if (window.CookieConsent) {
      window.CookieConsent.submitCustomConsent(
        cookiePreferences.preferences,
        cookiePreferences.statistics,
        cookiePreferences.marketing
      )
      setShowBanner(false)
    }
  }

  const toggleSection = (section: keyof typeof visibleSections) => {
    setVisibleSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const handleCheckboxChange = (type: keyof typeof cookiePreferences) => {
    if (type === 'necessary') return // Necessary cookies can't be toggled

    setCookiePreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  if (!showBanner) {
    return null
  }

  return (
    <div id="cookieBannerRoot">
      <div className="cookie-container" id="cookieContainer">
        <div id="CookieBannerOverlay" className="cookieBannerOverlay"></div>
        <div
          id="CookieBannerNotice"
          role="dialog"
          aria-labelledby="CookieBannerTitle"
          aria-describedby="CookieBannerDescription"
        >
          {/* Initial Dialog */}
          {!showCustomiseDialog ? (
            <div className="cookie-dialog" id="initialDialog">
              <div className="cookieBannerHeader">
                <div className="cookieBannerIcon">
                  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_3_2134)">
                      <mask
                        id="mask0_3_2134"
                        style={{ maskType: 'luminance' }}
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="30"
                        height="30"
                      >
                        <path d="M29.958 0.283203H0.146667V29.7172H29.958V0.283203Z" fill="white" />
                      </mask>
                      <g mask="url(#mask0_3_2134)">
                        <path
                          d="M28.5 15.5C28.5 23.232 23.2843 29 15 29C6.71573 29 1 22.732 1 15C2 7 7.5 2 14 1C8.98417 4.95987 14 12.5 20 11C18.5 16 24 18 28.5 15.5Z"
                          fill="#F58C82"
                        />
                        <path
                          d="M28.6164 14.0023C28.337 13.9937 28.0607 14.134 27.7941 14.4258C26.8305 15.4796 25.5381 15.9257 23.8439 15.7895C22.4022 15.6736 21.377 15.1419 20.7105 14.164C20.53 13.896 20.4083 13.5923 20.3536 13.2736C20.2988 12.9547 20.3122 12.6278 20.393 12.3146C20.4481 12.0742 20.5549 11.8487 20.7056 11.654C20.8566 11.4593 21.0479 11.2999 21.2664 11.187C21.2909 11.175 21.3107 11.1554 21.3232 11.1311C21.3356 11.1068 21.3398 11.079 21.3353 11.0521C21.1711 10.105 20.9458 9.91154 20.0634 9.95337C19.9954 9.95667 19.9275 9.96563 19.86 9.97384C19.8026 9.98095 19.7453 9.98831 19.6879 9.99174C18.4143 10.068 16.7522 10.072 15.1845 9.4168C13.5678 8.74118 12.7948 7.58389 12.8215 5.87918C12.8267 5.40548 12.88 4.93357 12.9803 4.47065C13.2485 3.32399 13.7599 2.24887 14.4799 1.31875L14.4953 1.29803C14.6714 1.06268 14.8008 0.870154 14.7345 0.712069C14.675 0.604883 14.5906 0.513818 14.4882 0.446785C14.3858 0.379751 14.2688 0.338741 14.1472 0.327317C14.0705 0.325109 13.9932 0.322179 13.9157 0.319113C13.4392 0.301084 12.9472 0.282321 12.492 0.408519C7.46456 1.80602 4.06288 4.5332 2.09239 8.74569C0.428467 12.303 -0.112368 15.347 0.390271 18.3254C1.17318 22.9641 3.64602 26.2105 7.74007 27.975C10.3323 29.0922 12.9289 29.6533 15.4925 29.6533C16.4715 29.6533 17.4488 29.5712 18.4142 29.4073C22.2166 28.7624 25.2087 27.0109 27.3071 24.201C28.9866 21.952 29.8175 19.1586 29.847 15.6613C29.8507 15.2292 29.6026 14.7574 29.3705 14.4594C29.1405 14.1661 28.8871 14.0128 28.6164 14.0023ZM14.786 11.0127C15.8598 11.4297 17.0574 11.6495 18.4471 11.6854C18.6438 11.6903 18.8406 11.6892 19.0485 11.6879L19.2041 11.6869C19.2026 11.6939 19.2013 11.7008 19.1998 11.7076C19.1809 11.8014 19.1662 11.8756 19.1462 11.9468C18.6539 13.6966 19.1677 15.2248 20.5926 16.2499C22.5379 17.6491 24.7834 17.891 27.2662 16.9689C27.3841 16.9251 27.4971 16.8729 27.6279 16.8122C27.6417 16.8058 27.6558 16.7999 27.67 16.7926C27.6524 17.8118 27.4896 18.8233 27.1858 19.7959C25.9919 23.7727 23.1371 26.3278 18.7009 27.3905C15.2622 28.2139 11.6817 27.822 8.05924 26.2254C4.79979 24.7892 2.7749 22.1239 2.04098 18.3036C1.73223 16.6872 1.77611 15.0228 2.16954 13.4251C3.1092 9.5451 4.50909 6.97072 6.70879 5.07776C8.6222 3.43118 10.5923 2.31486 12.7314 1.66523C12.7485 1.66008 12.7657 1.65552 12.785 1.65086C11.9976 2.77154 11.5138 4.07831 11.3809 5.4431C11.12 8.04188 12.3612 10.072 14.7854 11.0127H14.786Z"
                          fill="#006685"
                        />
                        <path
                          d="M16.9265 25.0178H16.9481C18.2343 25.0178 19.2545 23.8748 19.2747 22.4054C19.2877 21.4412 18.1614 20.2944 17.1601 20.2527C16.5851 20.2263 15.9543 20.5012 15.4312 21.0008C14.8797 21.5269 14.5468 22.2172 14.5407 22.8474C14.529 24.0512 15.5775 25.0042 16.9265 25.0178ZM16.2148 22.6497C16.2277 22.212 16.7733 21.6155 17.0743 21.6155H17.0754C17.2287 21.6427 17.3661 21.7263 17.4611 21.8499C17.6129 21.9988 17.7039 22.1988 17.7165 22.4112C17.6735 22.9223 17.329 23.2905 16.9154 23.2697C16.4729 23.2467 16.2043 23.0088 16.2148 22.6497Z"
                          fill="#006685"
                        />
                        <path
                          d="M10.0532 16.0609C10.0489 16.684 10.2364 17.2049 10.5963 17.5671C10.9623 17.9361 11.4954 18.1307 12.1391 18.1307H12.148C12.8172 18.1288 13.3841 17.9108 13.7878 17.5001C14.2336 17.0463 14.4648 16.379 14.456 15.5698C14.4438 14.3901 13.4406 13.636 11.9041 13.6506C10.8066 13.6605 10.0627 14.629 10.0532 16.0609ZM11.4747 16.059C11.4684 15.624 11.5724 15.2481 11.7462 15.0779C11.7717 15.0501 11.8025 15.0277 11.8367 15.012C11.871 14.9965 11.9079 14.9879 11.9456 14.9868H11.955C12.5126 15.0082 12.974 15.3654 12.9836 15.7831C12.9972 16.3691 12.6922 16.7435 12.1876 16.7599C11.9332 16.7689 11.7555 16.7214 11.6465 16.6169C11.5348 16.51 11.4785 16.3275 11.4747 16.059Z"
                          fill="#006685"
                        />
                        <path
                          d="M6.83125 17.7298C6.54456 17.725 6.25982 17.7777 5.99365 17.8848C5.7275 17.9919 5.48527 18.1511 5.28118 18.3532C5.11741 18.5108 4.98669 18.6995 4.89669 18.9086C4.80669 19.1176 4.75925 19.3425 4.75714 19.5703C4.76241 20.4778 5.30378 21.0192 6.20508 21.0192H6.2061C7.36959 21.0192 8.21663 20.2593 8.22042 19.2142C8.22322 18.3885 7.61293 17.7365 6.83125 17.7298ZM5.75342 19.4671C5.75842 19.3886 5.79403 19.3154 5.85252 19.2629C5.99107 19.1243 6.30456 18.9791 6.77422 18.9756C6.78754 18.9878 6.80218 19.0018 6.81782 19.0161C6.89284 19.0846 7.00622 19.1878 7.01905 19.2786C7.02456 19.3301 7.01863 19.3824 7.00174 19.4314C6.98484 19.4807 6.95742 19.5254 6.92133 19.5627C6.82108 19.6633 6.70188 19.7431 6.57067 19.7971C6.43944 19.851 6.29878 19.8784 6.15695 19.8775C6.04788 19.8875 5.9391 19.8556 5.85263 19.788C5.77863 19.6984 5.74295 19.5831 5.75342 19.4671Z"
                          fill="#006685"
                        />
                        <path
                          d="M8.54248 7.27197C8.10667 6.99443 7.53273 6.9919 6.96754 7.26526C6.39526 7.54218 6.20099 8.04452 6.01333 8.5303C5.98792 8.59603 5.96265 8.66179 5.93626 8.72654C5.86645 8.88818 5.82988 9.06231 5.82888 9.23848C5.82786 9.41467 5.86239 9.58924 5.93035 9.75169C6.00969 9.9272 6.12305 10.0851 6.26382 10.2161C6.40461 10.3472 6.56995 10.4488 6.75028 10.515C6.99135 10.6134 7.24877 10.6652 7.50899 10.6678C7.83405 10.6704 8.15231 10.5744 8.42205 10.3923C8.92297 10.0535 9.21437 9.44114 9.2212 8.71205C9.22731 8.07565 8.98012 7.55073 8.54248 7.27197ZM8.18001 8.8786C8.1695 9.38277 7.92673 9.65639 7.49861 9.64718C7.39735 9.64811 7.29692 9.62884 7.20312 9.59054C7.10933 9.55222 7.02411 9.49563 6.95228 9.42399C6.90833 9.37945 6.8739 9.32629 6.85122 9.2679C6.82852 9.20948 6.81803 9.14705 6.82031 9.08441C6.84007 8.87003 6.9368 8.67018 7.09252 8.52216C7.24824 8.37413 7.45228 8.28801 7.6666 8.27986H7.67984C7.8192 8.26614 7.95831 8.30816 8.06701 8.39675C8.14937 8.48309 8.18526 8.63611 8.18001 8.87882V8.8786Z"
                          fill="#006685"
                        />
                      </g>
                    </g>
                    <defs>
                      <clipPath id="clip0_3_2134">
                        <rect width="30" height="30" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <div id="CookieBannerTitle" className="cookieBannerTitle" role="status">
                  Cookie Consent
                </div>
              </div>

              <div id="CookieBannerDescription" className="cookiebannerDescription">
                We use cookies to enhance your browsing experience and analyse our traffic.
                {/* Please choose which cookies you would like to accept. */}
              </div>

              <div className="cookieButtonsContainer" id="cookieButtons">
                <button className="customiseCookiesBtn" id="customiseCookies" onClick={handleCustomise}>
                  Customise
                </button>
                <button className="acceptCookiesBtn" id="acceptCookies" onClick={handleAcceptAll}>
                  Accept All
                </button>
              </div>
            </div>
          ) : (
            /* Customise Dialog */
            <div className="cookie-dialog" id="customiseDialog">
              <div className="cookieBannerHeader">
                <div className="cookieBannerIcon">
                  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_3_2134)">
                      <mask
                        id="mask0_3_2134"
                        style={{ maskType: 'luminance' }}
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="30"
                        height="30"
                      >
                        <path d="M29.958 0.283203H0.146667V29.7172H29.958V0.283203Z" fill="white" />
                      </mask>
                      <g mask="url(#mask0_3_2134)">
                        <path
                          d="M28.5 15.5C28.5 23.232 23.2843 29 15 29C6.71573 29 1 22.732 1 15C2 7 7.5 2 14 1C8.98417 4.95987 14 12.5 20 11C18.5 16 24 18 28.5 15.5Z"
                          fill="#F58C82"
                        />
                        <path
                          d="M28.6164 14.0023C28.337 13.9937 28.0607 14.134 27.7941 14.4258C26.8305 15.4796 25.5381 15.9257 23.8439 15.7895C22.4022 15.6736 21.377 15.1419 20.7105 14.164C20.53 13.896 20.4083 13.5923 20.3536 13.2736C20.2988 12.9547 20.3122 12.6278 20.393 12.3146C20.4481 12.0742 20.5549 11.8487 20.7056 11.654C20.8566 11.4593 21.0479 11.2999 21.2664 11.187C21.2909 11.175 21.3107 11.1554 21.3232 11.1311C21.3356 11.1068 21.3398 11.079 21.3353 11.0521C21.1711 10.105 20.9458 9.91154 20.0634 9.95337C19.9954 9.95667 19.9275 9.96563 19.86 9.97384C19.8026 9.98095 19.7453 9.98831 19.6879 9.99174C18.4143 10.068 16.7522 10.072 15.1845 9.4168C13.5678 8.74118 12.7948 7.58389 12.8215 5.87918C12.8267 5.40548 12.88 4.93357 12.9803 4.47065C13.2485 3.32399 13.7599 2.24887 14.4799 1.31875L14.4953 1.29803C14.6714 1.06268 14.8008 0.870154 14.7345 0.712069C14.675 0.604883 14.5906 0.513818 14.4882 0.446785C14.3858 0.379751 14.2688 0.338741 14.1472 0.327317C14.0705 0.325109 13.9932 0.322179 13.9157 0.319113C13.4392 0.301084 12.9472 0.282321 12.492 0.408519C7.46456 1.80602 4.06288 4.5332 2.09239 8.74569C0.428467 12.303 -0.112368 15.347 0.390271 18.3254C1.17318 22.9641 3.64602 26.2105 7.74007 27.975C10.3323 29.0922 12.9289 29.6533 15.4925 29.6533C16.4715 29.6533 17.4488 29.5712 18.4142 29.4073C22.2166 28.7624 25.2087 27.0109 27.3071 24.201C28.9866 21.952 29.8175 19.1586 29.847 15.6613C29.8507 15.2292 29.6026 14.7574 29.3705 14.4594C29.1405 14.1661 28.8871 14.0128 28.6164 14.0023ZM14.786 11.0127C15.8598 11.4297 17.0574 11.6495 18.4471 11.6854C18.6438 11.6903 18.8406 11.6892 19.0485 11.6879L19.2041 11.6869C19.2026 11.6939 19.2013 11.7008 19.1998 11.7076C19.1809 11.8014 19.1662 11.8756 19.1462 11.9468C18.6539 13.6966 19.1677 15.2248 20.5926 16.2499C22.5379 17.6491 24.7834 17.891 27.2662 16.9689C27.3841 16.9251 27.4971 16.8729 27.6279 16.8122C27.6417 16.8058 27.6558 16.7999 27.67 16.7926C27.6524 17.8118 27.4896 18.8233 27.1858 19.7959C25.9919 23.7727 23.1371 26.3278 18.7009 27.3905C15.2622 28.2139 11.6817 27.822 8.05924 26.2254C4.79979 24.7892 2.7749 22.1239 2.04098 18.3036C1.73223 16.6872 1.77611 15.0228 2.16954 13.4251C3.1092 9.5451 4.50909 6.97072 6.70879 5.07776C8.6222 3.43118 10.5923 2.31486 12.7314 1.66523C12.7485 1.66008 12.7657 1.65552 12.785 1.65086C11.9976 2.77154 11.5138 4.07831 11.3809 5.4431C11.12 8.04188 12.3612 10.072 14.7854 11.0127H14.786Z"
                          fill="#006685"
                        />
                        <path
                          d="M16.9265 25.0178H16.9481C18.2343 25.0178 19.2545 23.8748 19.2747 22.4054C19.2877 21.4412 18.1614 20.2944 17.1601 20.2527C16.5851 20.2263 15.9543 20.5012 15.4312 21.0008C14.8797 21.5269 14.5468 22.2172 14.5407 22.8474C14.529 24.0512 15.5775 25.0042 16.9265 25.0178ZM16.2148 22.6497C16.2277 22.212 16.7733 21.6155 17.0743 21.6155H17.0754C17.2287 21.6427 17.3661 21.7263 17.4611 21.8499C17.6129 21.9988 17.7039 22.1988 17.7165 22.4112C17.6735 22.9223 17.329 23.2905 16.9154 23.2697C16.4729 23.2467 16.2043 23.0088 16.2148 22.6497Z"
                          fill="#006685"
                        />
                        <path
                          d="M10.0532 16.0609C10.0489 16.684 10.2364 17.2049 10.5963 17.5671C10.9623 17.9361 11.4954 18.1307 12.1391 18.1307H12.148C12.8172 18.1288 13.3841 17.9108 13.7878 17.5001C14.2336 17.0463 14.4648 16.379 14.456 15.5698C14.4438 14.3901 13.4406 13.636 11.9041 13.6506C10.8066 13.6605 10.0627 14.629 10.0532 16.0609ZM11.4747 16.059C11.4684 15.624 11.5724 15.2481 11.7462 15.0779C11.7717 15.0501 11.8025 15.0277 11.8367 15.012C11.871 14.9965 11.9079 14.9879 11.9456 14.9868H11.955C12.5126 15.0082 12.974 15.3654 12.9836 15.7831C12.9972 16.3691 12.6922 16.7435 12.1876 16.7599C11.9332 16.7689 11.7555 16.7214 11.6465 16.6169C11.5348 16.51 11.4785 16.3275 11.4747 16.059Z"
                          fill="#006685"
                        />
                        <path
                          d="M6.83125 17.7298C6.54456 17.725 6.25982 17.7777 5.99365 17.8848C5.7275 17.9919 5.48527 18.1511 5.28118 18.3532C5.11741 18.5108 4.98669 18.6995 4.89669 18.9086C4.80669 19.1176 4.75925 19.3425 4.75714 19.5703C4.76241 20.4778 5.30378 21.0192 6.20508 21.0192H6.2061C7.36959 21.0192 8.21663 20.2593 8.22042 19.2142C8.22322 18.3885 7.61293 17.7365 6.83125 17.7298ZM5.75342 19.4671C5.75842 19.3886 5.79403 19.3154 5.85252 19.2629C5.99107 19.1243 6.30456 18.9791 6.77422 18.9756C6.78754 18.9878 6.80218 19.0018 6.81782 19.0161C6.89284 19.0846 7.00622 19.1878 7.01905 19.2786C7.02456 19.3301 7.01863 19.3824 7.00174 19.4314C6.98484 19.4807 6.95742 19.5254 6.92133 19.5627C6.82108 19.6633 6.70188 19.7431 6.57067 19.7971C6.43944 19.851 6.29878 19.8784 6.15695 19.8775C6.04788 19.8875 5.9391 19.8556 5.85263 19.788C5.77863 19.6984 5.74295 19.5831 5.75342 19.4671Z"
                          fill="#006685"
                        />
                        <path
                          d="M8.54248 7.27197C8.10667 6.99443 7.53273 6.9919 6.96754 7.26526C6.39526 7.54218 6.20099 8.04452 6.01333 8.5303C5.98792 8.59603 5.96265 8.66179 5.93626 8.72654C5.86645 8.88818 5.82988 9.06231 5.82888 9.23848C5.82786 9.41467 5.86239 9.58924 5.93035 9.75169C6.00969 9.9272 6.12305 10.0851 6.26382 10.2161C6.40461 10.3472 6.56995 10.4488 6.75028 10.515C6.99135 10.6134 7.24877 10.6652 7.50899 10.6678C7.83405 10.6704 8.15231 10.5744 8.42205 10.3923C8.92297 10.0535 9.21437 9.44114 9.2212 8.71205C9.22731 8.07565 8.98012 7.55073 8.54248 7.27197ZM8.18001 8.8786C8.1695 9.38277 7.92673 9.65639 7.49861 9.64718C7.39735 9.64811 7.29692 9.62884 7.20312 9.59054C7.10933 9.55222 7.02411 9.49563 6.95228 9.42399C6.90833 9.37945 6.8739 9.32629 6.85122 9.2679C6.82852 9.20948 6.81803 9.14705 6.82031 9.08441C6.84007 8.87003 6.9368 8.67018 7.09252 8.52216C7.24824 8.37413 7.45228 8.28801 7.6666 8.27986H7.67984C7.8192 8.26614 7.95831 8.30816 8.06701 8.39675C8.14937 8.48309 8.18526 8.63611 8.18001 8.87882V8.8786Z"
                          fill="#006685"
                        />
                      </g>
                    </g>
                    <defs>
                      <clipPath id="clip0_3_2134">
                        <rect width="30" height="30" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <div id="CookieBannerTitle" className="cookieBannerTitle" role="status">
                  Cookie Consent
                </div>
              </div>

              <div id="CookieBannerDescriptionCustomise" className="CookieBannerDescriptionCustomise">
                Customise your cookie preferences. You can enable or disable different types of cookies below.
              </div>

              {/* Cookie option sections */}
              <div className="sectionContainerTop">
                <div className="sectionTopBar">
                  <div className="sectionLeftSide">
                    <div className="sectionOptionTitle">Necessary</div>
                    <div
                      className={`sectionOptionArrow ${visibleSections.necessary ? 'hidden' : ''}`}
                      onClick={() => toggleSection('necessary')}
                    >
                      <svg width="24" height="10" viewBox="0 0 30 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M29.4004 3.35931L16.2464 15.5152C16.0715 15.6768 15.8778 15.798 15.6655 15.8788C15.4531 15.9596 15.2345 16 15.0097 16C14.7848 16 14.5662 15.9596 14.3538 15.8788C14.1415 15.798 13.9478 15.6768 13.7729 15.5152L0.61892 3.35931C0.219177 2.9899 0.0130598 2.52814 0.000567816 1.97403C-0.0119241 1.41991 0.181701 0.958152 0.581444 0.588744C1.00617 0.196248 1.51834 3.83353e-07 2.11795 3.83353e-07C2.71757 3.83353e-07 3.22974 0.196248 3.65447 0.588744L15.0097 11.0823L26.3648 0.588744C26.7896 0.196248 27.2955 3.83353e-07 27.8826 3.83353e-07C28.4697 3.83353e-07 28.9632 0.184704 29.3629 0.554112C29.7876 0.946609 30 1.41991 30 1.97403C30 2.52814 29.8001 2.9899 29.4004 3.35931Z"
                          fill="#006685"
                        />
                      </svg>
                    </div>
                    <div
                      className={`sectionOptionArrow ${!visibleSections.necessary ? 'hidden' : ''}`}
                      onClick={() => toggleSection('necessary')}
                    >
                      <svg width="24" height="10" viewBox="0 0 30 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M0.599613 12.6407L13.7536 0.484848C13.9285 0.323232 14.1222 0.20202 14.3345 0.121212C14.5469 0.0404041 14.7655 0 14.9903 0C15.2152 0 15.4338 0.0404041 15.6462 0.121212C15.8585 0.20202 16.0522 0.323232 16.2271 0.484848L29.3811 12.6407C29.7808 13.0101 29.9869 13.4719 29.9994 14.026C30.0119 14.5801 29.8183 15.0418 29.4186 15.4113C28.9938 15.8038 28.4817 16 27.882 16C27.2824 16 26.7703 15.8038 26.3455 15.4113L14.9903 4.91775L3.63516 15.4113C3.21043 15.8038 2.70451 16 2.11739 16C1.53026 16 1.03683 15.8153 0.637088 15.4459C0.212362 15.0534 -4.4387e-07 14.5801 -4.4387e-07 14.026C-4.4387e-07 13.4719 0.19987 13.0101 0.599613 12.6407Z"
                          fill="#006685"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="sectionRightSide">
                    <label className="switch">
                      <input id="necessaryCheckbox" type="checkbox" checked disabled />
                      <span id="switchDisabled" className="slider"></span>
                    </label>
                  </div>
                </div>
                <div className={`textSection ${!visibleSections.necessary ? 'hidden' : ''}`}>
                  <p className="textSectionText">
                    Necessary cookies help make a website usable by enabling basic functions like page navigation and
                    access to secure areas of the website. The website cannot function properly without these cookies.
                  </p>
                </div>
              </div>

              <div className="sectionContainer">
                <div className="sectionTopBar">
                  <div className="sectionLeftSide">
                    <div className="sectionOptionTitle">Preferences</div>
                    <div
                      className={`sectionOptionArrow ${visibleSections.preferences ? 'hidden' : ''}`}
                      onClick={() => toggleSection('preferences')}
                    >
                      <svg width="24" height="10" viewBox="0 0 30 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M29.4004 3.35931L16.2464 15.5152C16.0715 15.6768 15.8778 15.798 15.6655 15.8788C15.4531 15.9596 15.2345 16 15.0097 16C14.7848 16 14.5662 15.9596 14.3538 15.8788C14.1415 15.798 13.9478 15.6768 13.7729 15.5152L0.61892 3.35931C0.219177 2.9899 0.0130598 2.52814 0.000567816 1.97403C-0.0119241 1.41991 0.181701 0.958152 0.581444 0.588744C1.00617 0.196248 1.51834 3.83353e-07 2.11795 3.83353e-07C2.71757 3.83353e-07 3.22974 0.196248 3.65447 0.588744L15.0097 11.0823L26.3648 0.588744C26.7896 0.196248 27.2955 3.83353e-07 27.8826 3.83353e-07C28.4697 3.83353e-07 28.9632 0.184704 29.3629 0.554112C29.7876 0.946609 30 1.41991 30 1.97403C30 2.52814 29.8001 2.9899 29.4004 3.35931Z"
                          fill="#006685"
                        />
                      </svg>
                    </div>
                    <div
                      className={`sectionOptionArrow ${!visibleSections.preferences ? 'hidden' : ''}`}
                      onClick={() => toggleSection('preferences')}
                    >
                      <svg width="24" height="10" viewBox="0 0 30 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M0.599613 12.6407L13.7536 0.484848C13.9285 0.323232 14.1222 0.20202 14.3345 0.121212C14.5469 0.0404041 14.7655 0 14.9903 0C15.2152 0 15.4338 0.0404041 15.6462 0.121212C15.8585 0.20202 16.0522 0.323232 16.2271 0.484848L29.3811 12.6407C29.7808 13.0101 29.9869 13.4719 29.9994 14.026C30.0119 14.5801 29.8183 15.0418 29.4186 15.4113C28.9938 15.8038 28.4817 16 27.882 16C27.2824 16 26.7703 15.8038 26.3455 15.4113L14.9903 4.91775L3.63516 15.4113C3.21043 15.8038 2.70451 16 2.11739 16C1.53026 16 1.03683 15.8153 0.637088 15.4459C0.212362 15.0534 -4.4387e-07 14.5801 -4.4387e-07 14.026C-4.4387e-07 13.4719 0.19987 13.0101 0.599613 12.6407Z"
                          fill="#006685"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="sectionRightSide">
                    <label className="switch">
                      <input
                        id="preferenceCheckbox"
                        type="checkbox"
                        checked={cookiePreferences.preferences}
                        onChange={() => handleCheckboxChange('preferences')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
                <div className={`textSection ${!visibleSections.preferences ? 'hidden' : ''}`}>
                  <p className="textSectionText">
                    Preference cookies enable a website to remember information that changes the way the website behaves
                    or looks, like your preferred language or the region that you are in.
                  </p>
                </div>
              </div>

              <div className="sectionContainer">
                <div className="sectionTopBar">
                  <div className="sectionLeftSide">
                    <div className="sectionOptionTitle">Statistics</div>
                    <div
                      className={`sectionOptionArrow ${visibleSections.statistics ? 'hidden' : ''}`}
                      onClick={() => toggleSection('statistics')}
                    >
                      <svg width="24" height="10" viewBox="0 0 30 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M29.4004 3.35931L16.2464 15.5152C16.0715 15.6768 15.8778 15.798 15.6655 15.8788C15.4531 15.9596 15.2345 16 15.0097 16C14.7848 16 14.5662 15.9596 14.3538 15.8788C14.1415 15.798 13.9478 15.6768 13.7729 15.5152L0.61892 3.35931C0.219177 2.9899 0.0130598 2.52814 0.000567816 1.97403C-0.0119241 1.41991 0.181701 0.958152 0.581444 0.588744C1.00617 0.196248 1.51834 3.83353e-07 2.11795 3.83353e-07C2.71757 3.83353e-07 3.22974 0.196248 3.65447 0.588744L15.0097 11.0823L26.3648 0.588744C26.7896 0.196248 27.2955 3.83353e-07 27.8826 3.83353e-07C28.4697 3.83353e-07 28.9632 0.184704 29.3629 0.554112C29.7876 0.946609 30 1.41991 30 1.97403C30 2.52814 29.8001 2.9899 29.4004 3.35931Z"
                          fill="#006685"
                        />
                      </svg>
                    </div>
                    <div
                      className={`sectionOptionArrow ${!visibleSections.statistics ? 'hidden' : ''}`}
                      onClick={() => toggleSection('statistics')}
                    >
                      <svg width="24" height="10" viewBox="0 0 30 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M0.599613 12.6407L13.7536 0.484848C13.9285 0.323232 14.1222 0.20202 14.3345 0.121212C14.5469 0.0404041 14.7655 0 14.9903 0C15.2152 0 15.4338 0.0404041 15.6462 0.121212C15.8585 0.20202 16.0522 0.323232 16.2271 0.484848L29.3811 12.6407C29.7808 13.0101 29.9869 13.4719 29.9994 14.026C30.0119 14.5801 29.8183 15.0418 29.4186 15.4113C28.9938 15.8038 28.4817 16 27.882 16C27.2824 16 26.7703 15.8038 26.3455 15.4113L14.9903 4.91775L3.63516 15.4113C3.21043 15.8038 2.70451 16 2.11739 16C1.53026 16 1.03683 15.8153 0.637088 15.4459C0.212362 15.0534 -4.4387e-07 14.5801 -4.4387e-07 14.026C-4.4387e-07 13.4719 0.19987 13.0101 0.599613 12.6407Z"
                          fill="#006685"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="sectionRightSide">
                    <label className="switch">
                      <input
                        id="statisticsCheckbox"
                        type="checkbox"
                        checked={cookiePreferences.statistics}
                        onChange={() => handleCheckboxChange('statistics')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
                <div className={`textSection ${!visibleSections.statistics ? 'hidden' : ''}`}>
                  <p className="textSectionText">
                    Statistic cookies help website owners to understand how visitors interact with websites by
                    collecting and reporting information anonymously.
                  </p>
                </div>
              </div>

              <div className="sectionContainer">
                <div className="sectionTopBar">
                  <div className="sectionLeftSide">
                    <div className="sectionOptionTitle">Marketing</div>
                    <div
                      className={`sectionOptionArrow ${visibleSections.marketing ? 'hidden' : ''}`}
                      onClick={() => toggleSection('marketing')}
                    >
                      <svg width="24" height="10" viewBox="0 0 30 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M29.4004 3.35931L16.2464 15.5152C16.0715 15.6768 15.8778 15.798 15.6655 15.8788C15.4531 15.9596 15.2345 16 15.0097 16C14.7848 16 14.5662 15.9596 14.3538 15.8788C14.1415 15.798 13.9478 15.6768 13.7729 15.5152L0.61892 3.35931C0.219177 2.9899 0.0130598 2.52814 0.000567816 1.97403C-0.0119241 1.41991 0.181701 0.958152 0.581444 0.588744C1.00617 0.196248 1.51834 3.83353e-07 2.11795 3.83353e-07C2.71757 3.83353e-07 3.22974 0.196248 3.65447 0.588744L15.0097 11.0823L26.3648 0.588744C26.7896 0.196248 27.2955 3.83353e-07 27.8826 3.83353e-07C28.4697 3.83353e-07 28.9632 0.184704 29.3629 0.554112C29.7876 0.946609 30 1.41991 30 1.97403C30 2.52814 29.8001 2.9899 29.4004 3.35931Z"
                          fill="#006685"
                        />
                      </svg>
                    </div>
                    <div
                      className={`sectionOptionArrow ${!visibleSections.marketing ? 'hidden' : ''}`}
                      onClick={() => toggleSection('marketing')}
                    >
                      <svg width="24" height="10" viewBox="0 0 30 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M0.599613 12.6407L13.7536 0.484848C13.9285 0.323232 14.1222 0.20202 14.3345 0.121212C14.5469 0.0404041 14.7655 0 14.9903 0C15.2152 0 15.4338 0.0404041 15.6462 0.121212C15.8585 0.20202 16.0522 0.323232 16.2271 0.484848L29.3811 12.6407C29.7808 13.0101 29.9869 13.4719 29.9994 14.026C30.0119 14.5801 29.8183 15.0418 29.4186 15.4113C28.9938 15.8038 28.4817 16 27.882 16C27.2824 16 26.7703 15.8038 26.3455 15.4113L14.9903 4.91775L3.63516 15.4113C3.21043 15.8038 2.70451 16 2.11739 16C1.53026 16 1.03683 15.8153 0.637088 15.4459C0.212362 15.0534 -4.4387e-07 14.5801 -4.4387e-07 14.026C-4.4387e-07 13.4719 0.19987 13.0101 0.599613 12.6407Z"
                          fill="#006685"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="sectionRightSide">
                    <label className="switch">
                      <input
                        id="marketingCheckbox"
                        type="checkbox"
                        checked={cookiePreferences.marketing}
                        onChange={() => handleCheckboxChange('marketing')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
                <div className={`textSection ${!visibleSections.marketing ? 'hidden' : ''}`}>
                  <p className="textSectionText">
                    Marketing cookies are used to track visitors across websites. The intention is to display ads that
                    are relevant and engaging for the individual user and thereby more valuable for publishers and third
                    party advertisers.
                  </p>
                </div>
              </div>

              {/* <div className="sectionContainer">
              <div className="sectionTopBar">
                <div className="sectionLeftSide">
                  <div className="sectionOptionTitle">Unclassified</div>
                  <div
                    className={`sectionOptionArrow ${visibleSections.unclassified ? 'hidden' : ''}`}
                    onClick={() => toggleSection('unclassified')}
                  >
                    <svg width="24" height="10" viewBox="0 0 30 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M29.4004 3.35931L16.2464 15.5152C16.0715 15.6768 15.8778 15.798 15.6655 15.8788C15.4531 15.9596 15.2345 16 15.0097 16C14.7848 16 14.5662 15.9596 14.3538 15.8788C14.1415 15.798 13.9478 15.6768 13.7729 15.5152L0.61892 3.35931C0.219177 2.9899 0.0130598 2.52814 0.000567816 1.97403C-0.0119241 1.41991 0.181701 0.958152 0.581444 0.588744C1.00617 0.196248 1.51834 3.83353e-07 2.11795 3.83353e-07C2.71757 3.83353e-07 3.22974 0.196248 3.65447 0.588744L15.0097 11.0823L26.3648 0.588744C26.7896 0.196248 27.2955 3.83353e-07 27.8826 3.83353e-07C28.4697 3.83353e-07 28.9632 0.184704 29.3629 0.554112C29.7876 0.946609 30 1.41991 30 1.97403C30 2.52814 29.8001 2.9899 29.4004 3.35931Z"
                        fill="#006685"
                      />
                    </svg>
                  </div>
                  <div
                    className={`sectionOptionArrow ${!visibleSections.unclassified ? 'hidden' : ''}`}
                    onClick={() => toggleSection('unclassified')}
                  >
                    <svg width="24" height="10" viewBox="0 0 30 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M0.599613 12.6407L13.7536 0.484848C13.9285 0.323232 14.1222 0.20202 14.3345 0.121212C14.5469 0.0404041 14.7655 0 14.9903 0C15.2152 0 15.4338 0.0404041 15.6462 0.121212C15.8585 0.20202 16.0522 0.323232 16.2271 0.484848L29.3811 12.6407C29.7808 13.0101 29.9869 13.4719 29.9994 14.026C30.0119 14.5801 29.8183 15.0418 29.4186 15.4113C28.9938 15.8038 28.4817 16 27.882 16C27.2824 16 26.7703 15.8038 26.3455 15.4113L14.9903 4.91775L3.63516 15.4113C3.21043 15.8038 2.70451 16 2.11739 16C1.53026 16 1.03683 15.8153 0.637088 15.4459C0.212362 15.0534 -4.4387e-07 14.5801 -4.4387e-07 14.026C-4.4387e-07 13.4719 0.19987 13.0101 0.599613 12.6407Z"
                        fill="#006685"
                      />
                    </svg>
                  </div>
                </div>
                <div className="sectionRightSide">
                  <label className="switch">
                    <input
                      id="unclassifiedCheckbox"
                      type="checkbox"
                      checked={cookiePreferences.unclassified}
                      onChange={() => handleCheckboxChange('unclassified')}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
              <div className={`textSection ${!visibleSections.unclassified ? 'hidden' : ''}`}>
                <p className="textSectionText">
                  Unclassified cookies are cookies that we are in the process of classifying, together with the
                  providers of individual cookies.
                </p>
              </div>
            </div> */}

              <div className="cookieTextSection">
                <p className="cookieText">
                  Visit our{' '}
                  <a href="https://www.differentdog.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                    <span className="underlineText">Cookies</span>
                  </a>{' '}
                  page for more details
                </p>
              </div>

              <div className="cookieButtonsContainer">
                <button className="customiseCookiesBtn" onClick={handleAcceptAll}>
                  Accept All
                </button>
                <button className="acceptCookiesBtn" onClick={handleAcceptSelected}>
                  Accept Selected
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CookieBanner
